<template>
  <Dialog :visible="manageUserGroupQuotasDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage {{ manageUserGroupName }} Quotas
    </template>


    <div class="full-width flex">
      <Dropdown :options="availableQuotas"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedQuota"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addQuotaToGroupSubmitting" @click="addQuotaToGroup(selectedQuota)" :disabled="!selectedQuota" label="Add Quota"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <DataTable :value="userGroupQuotaAssignments">
        <Column field="name" header="Name"></Column>
        <Column field="dailyQuota" header="Daily Limit"></Column>
        <Column field="monthlyQuota" header="Monthly Limit"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteUserGroupQuota(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeQuotaFromGroupSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>
  <Dialog :visible="manageUserGroupDeploymentsDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage {{ manageUserGroupName }} LLM Deployments
    </template>


    <div class="full-width flex">
      <Dropdown :options="availableDeployments"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedDeployment"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addDeploymentToGroupSubmitting" @click="addDeploymentToGroup(selectedDeployment)" :disabled="!selectedDeployment" label="Add Deployment"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <DataTable :value="userGroupDeploymentAccess">
        <Column field="name" header="Name"></Column>
        <Column field="modelName" header="Model"></Column>
        <Column field="status" header="Status"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteUserGroupDeploymentAccess(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeDeploymentFromGroupSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>
  <Dialog :visible="manageUserGroupMembershipsDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage {{ manageUserGroupName }} Assignments
    </template>


    <div class="full-width flex">
      <Dropdown :options="availableUsers"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedUser"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addUserToGroupSubmitting" @click="addUserToGroup(selectedUser)" :disabled="!selectedUser" label="Add User"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <DataTable :value="userGroupAssignments">
        <Column field="name" header="Name"></Column>
        <Column field="email" header="Email"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteUserGroupMembership(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeUserFromGroupSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>
  <Dialog :visible="manageUserGroupDialogVisible"
          class="flex-dialog"
          @update:visible="handleClose">
    <template #header>
      {{ manageUserGroupId ? 'Edit' : 'Add' }} User Group
    </template>

    <div class="form-element">
      Name:
      <InputText v-model="manageUserGroupName" :invalid="manageUserGroupNameInvalid" class="form-element full-width" type="text"
                 @change="manageUserGroupNameChange"/>
      <div v-if="manageUserGroupNameInvalid">
        <InlineMessage severity="error">Name is required.</InlineMessage>
      </div>
    </div>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageUserGroupSubmitting" :loading="manageUserGroupSubmitting" label="Save" type="button" @click="saveUserGroup"></Button>
      </div>
    </template>
  </Dialog>

  <div v-if="canManageUsers">
    <ProgressSpinnerContainer v-if="usersLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">Users Groups</h2>
        <div class="align-right mr">
          <Button @click="manageUserGroup(null)"><i class="pi pi-plus"></i> Add User Group</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="userGroups">
          <Column field="name" header="Name"></Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button severity="info" @click="manageUserGroup(slotProps.data)" label="Edit"></Button>
              <Button class="ml" severity="info" @click="manageUserGroupMemberships(slotProps.data)" label="Manage Users"></Button>
              <Button class="ml" severity="info" @click="manageUserGroupQuotas(slotProps.data)" label="Manage Quotas"></Button>
              <Button class="ml" severity="info" @click="manageUserGroupDeployments(slotProps.data)" label="Manage LLM Deployments"></Button>
              <Button class="ml" severity="danger" @click="deleteUserGroup(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useAuth0 } from "@auth0/auth0-vue";
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import { useToast } from "primevue/usetoast";
import { computed, ref, unref, watch } from "vue";
import { loadUsers, useLlmQuotas, useUsers, useLlmModelDeploymentAccess, useModelDeployments, loadLlmQuotas, loadLlmModelDeploymentAccess, loadModelDeployments } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post } from "../util/api.js";
import { decodeLlmQuota, decodeLlmQuotaAssignment, decodeUserGroup, decodeUserGroupMembership, decodeLlmModelDeploymentAccess } from "../util/util.js";

const { users, userGroups, userGroupMemberships, usersLoading, canManageUsers } = useUsers()

const { llmModelDeploymentAccess } = useLlmModelDeploymentAccess()
const { modelDeployments } = useModelDeployments()

const toast = useToast()

const confirm = useConfirm()

async function deleteUserGroup(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.name + '?',
    header: 'Confirm Delete User Group',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        usersLoading.value = true
        const request = await deleteRequest('/v1/cephisso-platform-user-groups/' + data.id)
        if (request.id) {
          userGroups.value = userGroups.value.filter(q => String(q.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        usersLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

watch(usersLoading, (newValue)=> {
  if (!newValue) {
    if (!canManageUsers.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

// Load additional data when component loads
watch(selectedAccount, async (newValue) => {
  if (newValue) {
    await Promise.all([
      loadLlmQuotas(),
      loadLlmModelDeploymentAccess(),
      loadModelDeployments()
    ])
  }
}, { immediate: true })

const manageUserGroupDialogVisible = ref(false)
const manageUserGroupSubmitting = ref(false)

const manageUserGroupMembershipsDialogVisible = ref(false)
const addUserToGroupSubmitting = ref(false)
const removeUserFromGroupSubmitting = ref(false)
const selectedUser = ref(null)


const manageUserGroupQuotasDialogVisible = ref(false)
const addQuotaToGroupSubmitting = ref(false)
const removeQuotaFromGroupSubmitting = ref(false)
const selectedQuota = ref(false)

const manageUserGroupDeploymentsDialogVisible = ref(false)
const addDeploymentToGroupSubmitting = ref(false)
const removeDeploymentFromGroupSubmitting = ref(false)
const selectedDeployment = ref(null)

const manageUserGroupId = ref(null)
const manageUserGroupName = ref(null)

const manageUserGroupNameInvalid = ref(false)

function manageUserGroupNameChange() {
  manageUserGroupNameInvalid.value = !manageUserGroupName.value
}

function manageUserGroup(data) {
  manageUserGroupDialogVisible.value = true
  manageUserGroupId.value = data?.id
  manageUserGroupName.value = data?.name
}
function manageUserGroupMemberships(data) {
  manageUserGroupMembershipsDialogVisible.value = true
  manageUserGroupId.value = data?.id
  manageUserGroupName.value = data?.name
}
function manageUserGroupQuotas(data) {
  manageUserGroupQuotasDialogVisible.value = true
  manageUserGroupId.value = data?.id
  manageUserGroupName.value = data?.name
}
function manageUserGroupDeployments(data) {
  manageUserGroupDeploymentsDialogVisible.value = true
  manageUserGroupId.value = data?.id
  manageUserGroupName.value = data?.name
}
function handleClose() {
  manageUserGroupId.value = null
  manageUserGroupName.value = null

  manageUserGroupNameInvalid.value = false

  manageUserGroupSubmitting.value = false
  manageUserGroupDialogVisible.value = false

  manageUserGroupMembershipsDialogVisible.value = false
  addUserToGroupSubmitting.value = false
  removeUserFromGroupSubmitting.value = false
  selectedUser.value = null

  manageUserGroupQuotasDialogVisible.value = false
  addQuotaToGroupSubmitting.value = false
  removeQuotaFromGroupSubmitting.value = false
  selectedQuota.value = false

  manageUserGroupDeploymentsDialogVisible.value = false
  addDeploymentToGroupSubmitting.value = false
  removeDeploymentFromGroupSubmitting.value = false
  selectedDeployment.value = null
}


function handleError(e) {
  addUserToGroupSubmitting.value = false
  manageUserGroupSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}

function resetValidations() {
  manageUserGroupNameInvalid.value = false
}

async function saveUserGroup() {
  let valid = true
  resetValidations()
  if (!manageUserGroupName.value) {
    manageUserGroupNameInvalid.value = true
    valid = false
  }

  if (!valid) {
    return false
  }

  if (manageUserGroupId.value) {
    const payload = {
      name: manageUserGroupName.value,
    }
    manageUserGroupSubmitting.value = true
    try {
      const result = await patch('/v1/cephisso-platform-user-groups/' + manageUserGroupId.value, payload)
      if (result.id) {
        for (let i = 0; i < userGroups.value.length; i++) {
          if (String(userGroups.value[i].id) === String(result.id)) {
            userGroups.value[i] = decodeUserGroup(result)
          }
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageUserGroupSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const payload = {
      name: manageUserGroupName.value,
      cephisso_platform_accounts_id: selectedAccount.value
    }
    manageUserGroupSubmitting.value = true
    try {
      const result = await post('/v1/cephisso-platform-user-groups', payload)
      if (result.id) {
        userGroups.value.push(decodeUserGroup(result))
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageUserGroupSubmitting.value = false
      handleError(e)
    }
  }
}

const userGroupAssignments = computed(() => {
  const output = []
  for (let i = 0; i < userGroupMemberships.value?.length; i++) {
    if (String(userGroupMemberships.value[i].groupId) === String(manageUserGroupId.value)) {
      const g = users.value.find(u => String(u.id) === String(userGroupMemberships.value[i].userId))
      if (g) {
        output.push(g)
      }
    }
  }
  return output
})

async function addUserToGroup(id) {
  const payload = {
    cephisso_platform_users_id: id,
    cephisso_platform_user_groups_id: manageUserGroupId.value,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addUserToGroupSubmitting.value = true
  const request = await post('/v1/cephisso-platform-user-group-memberships', payload)
  if (request.id) {
    userGroupMemberships.value.push(decodeUserGroupMembership(request))
  }
  else {
    handleError()
  }
  addUserToGroupSubmitting.value = false
  selectedUser.value = null
}

async function deleteUserGroupMembership(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + data.name + ' from ' + manageUserGroupName.value + '?',
    header: 'Confirm User Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeUserFromGroupSubmitting.value = true
        for (let i = 0; i < userGroupMemberships.value?.length; i++) {
          if (
              (String(userGroupMemberships.value[i].groupId) === String(manageUserGroupId.value)) &&
              (String(userGroupMemberships.value[i].userId) === String(data.id))
          ) {
            const request = await deleteRequest('/v1/cephisso-platform-user-group-memberships/' + userGroupMemberships.value[i].id)
            if (request.id) {
              userGroupMemberships.value = userGroupMemberships.value.filter(gm => String(gm.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeUserFromGroupSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

const availableUsers = computed(() => users.value.filter(u => !userGroupAssignments.value.find(a => String(a.id) === String(u.id))))

const { llmQuotas, llmQuotaAssignments } = useLlmQuotas()

const userGroupQuotaAssignments = computed(() => {
  const output = []
  for (let i = 0; i < llmQuotaAssignments.value?.length; i++) {
    if (String(llmQuotaAssignments.value[i].groupId) === String(manageUserGroupId.value)) {
      const q = llmQuotas.value.find(q => String(q.id) === String(llmQuotaAssignments.value[i].llmQuotaId))
      if (q) {
        output.push(q)
      }
    }
  }
  return output
})

const availableQuotas = computed(() => llmQuotas.value.filter(q => !userGroupQuotaAssignments.value.find(a => String(a.id) === String(q.id))))

const userGroupDeploymentAccess = computed(() => {
  const output = []
  for (let i = 0; i < llmModelDeploymentAccess.value?.length; i++) {
    if (String(llmModelDeploymentAccess.value[i].groupId) === String(manageUserGroupId.value)) {
      const d = modelDeployments.value.find(d => String(d.id) === String(llmModelDeploymentAccess.value[i].llmModelDeploymentId))
      if (d) {
        output.push({
          deploymentAccessId: llmModelDeploymentAccess.value[i].id,
          ...d
        })
      }
    }
  }
  return output
})

const availableDeployments = computed(() => modelDeployments.value.filter(d => 
  !userGroupDeploymentAccess.value.find(a => String(a.id) === String(d.id)) &&
  d.platformAccountId === selectedAccount.value &&
  d.status === 'ACTIVE'
))

async function addQuotaToGroup(id) {
  const payload = {
    ai_credit_quotas_id: id,
    cephisso_platform_user_groups_id: manageUserGroupId.value,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addQuotaToGroupSubmitting.value = true
  const request = await post('/crbn-tech/ai-credit-quota-assignments', payload)
  if (request.id) {
    llmQuotaAssignments.value.push(decodeLlmQuotaAssignment(request))
  }
  else {
    handleError()
  }
  addQuotaToGroupSubmitting.value = false
  selectedQuota.value = null
}

async function addDeploymentToGroup(id) {
  const payload = {
    llm_model_deployments_id: id,
    cephisso_platform_user_groups_id: manageUserGroupId.value,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addDeploymentToGroupSubmitting.value = true
  const request = await post('/crbn-tech/llm-model-deployment-access', payload)
  if (request.id) {
    llmModelDeploymentAccess.value.push(decodeLlmModelDeploymentAccess(request))
  }
  else {
    handleError()
  }
  addDeploymentToGroupSubmitting.value = false
  selectedDeployment.value = null
}

async function deleteUserGroupQuota(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + data.name + ' from ' + manageUserGroupName.value + '?',
    header: 'Confirm Quota Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeQuotaFromGroupSubmitting.value = true
        for (let i = 0; i < llmQuotaAssignments.value?.length; i++) {
          if (
              (String(llmQuotaAssignments.value[i].groupId) === String(manageUserGroupId.value)) &&
              (String(llmQuotaAssignments.value[i].llmQuotaId) === String(data.id))
          ) {
            const request = await deleteRequest('/crbn-tech/ai-credit-quota-assignments/' + llmQuotaAssignments.value[i].id)
            if (request.id) {
              llmQuotaAssignments.value = llmQuotaAssignments.value.filter(qa => String(qa.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeQuotaFromGroupSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

function deleteUserGroupDeploymentAccess(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + data.name + ' from ' + manageUserGroupName.value + '?',
    header: 'Confirm Deployment Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeDeploymentFromGroupSubmitting.value = true
        for (let i = 0; i < llmModelDeploymentAccess.value?.length; i++) {
          if (
              (String(llmModelDeploymentAccess.value[i].groupId) === String(manageUserGroupId.value)) &&
              (String(llmModelDeploymentAccess.value[i].llmModelDeploymentId) === String(data.id))
          ) {
            const request = await deleteRequest('/crbn-tech/llm-model-deployment-access/' + llmModelDeploymentAccess.value[i].id)
            if (request.id) {
              llmModelDeploymentAccess.value = llmModelDeploymentAccess.value.filter(da => String(da.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeDeploymentFromGroupSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

</script>

<style scoped>
</style>